import { CommonModule } from '@angular/common';
import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterModule } from '@angular/router';
import { CheckboxModule } from 'primeng/checkbox';
import { StepsModule } from 'primeng/steps';
import {
  catchError,
  concatAll,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { RecommendationStatus } from 'src/app/core/models/recommendation.model';
import { CurrentUser } from 'src/app/core/models/user.model';
import {
  WebhookEntityType,
  WebhookWorkflowChangeType,
  WebhookWorkflowObjectAddedType
} from 'src/app/core/models/webhook.model';
import { IntegrationsService } from 'src/app/core/services/integrations.service';
import { UserInfoService } from 'src/app/core/services/user-info.service';
import { UsersService } from 'src/app/core/services/users.service';
import { NumbersOnlyDirective } from 'src/app/core/utils/directives/numbers-only/numbers-only.directive';

@Pipe({
  name: 'welcomeTooltip',
  standalone: true
})
export class WelcomeTooltipPipe implements PipeTransform {
  transform(value: boolean): string {
    if (value) {
      return $localize`Must consent to receive SMS messages`;
    }

    return '';
  }
}

@Component({
  selector: 'notifications-welcome-modal',
  templateUrl: './notifications-welcome-modal.component.html',
  styleUrls: ['./notifications-welcome-modal.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    StepsModule,
    CheckboxModule,
    NumbersOnlyDirective,
    WelcomeTooltipPipe,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule
  ]
})
export class NotificationsWelcomeModalComponent implements OnInit {
  stepItems = [
    {
      label: $localize`Introduction`
    },
    {
      label: $localize`Quick Settings`
    }
  ];
  activeIndex = 0;

  awaitingResponse = false;

  all = false;
  closed = false;
  created = false;

  notificationForm: FormGroup;
  ngUnsubscribe = new Subject<void>();

  constructor(
    private integrationsService: IntegrationsService,
    private usersService: UsersService,
    public userInfoService: UserInfoService,
    public dialogRef: MatDialogRef<NotificationsWelcomeModalComponent>
  ) {}

  ngOnInit(): void {
    const { phone, smsConsent } = this.userInfoService.info$.getValue();

    this.notificationForm = new FormGroup(
      {
        name: new FormControl('new'),
        description: new FormControl('new'),
        enabled: new FormControl(true),
        sites: new FormControl([]),
        entityType: new FormControl(WebhookEntityType.Recommendation),
        workflowChange: new FormGroup({
          type: new FormControl(WebhookWorkflowChangeType.StatusChange),
          value: new FormControl([])
        }),
        notificationType: new FormGroup({
          email: new FormControl(false),
          sms: new FormControl(false)
        }),
        phoneNumber: new FormControl(phone),
        smsConsent: new FormControl(smsConsent ?? false)
      },
      { validators: this.notificationTypeValidator() }
    );
  }

  notificationTypeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const email = group.get('notificationType')?.value?.email as boolean;
      const sms = group.get('notificationType')?.value?.sms as boolean;
      const change = this.all || this.closed || this.created ? true : false;

      return (email || sms) && change ? null : { atLeastOneRequired: true };
    };
  }

  allSelected(): void {
    if (this.all) {
      this.closed = true;
      this.created = true;
    } else {
      this.closed = false;
      this.created = false;
    }

    this.notificationForm.updateValueAndValidity();
  }

  doNotShowAgain(): void {
    this.awaitingResponse = true;
    const user = {
      displayPreferences: {
        ...this.userInfoService.info$.getValue().displayPreferences,
        bypassnotificationwelcomeprompt: true
      }
    };

    this.usersService
      .patchCurrentUser(user)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap(() => {
          this.dialogRef.close();
          this.awaitingResponse = false;
        })
      )
      .subscribe();
  }

  resetPhoneErrors() {
    this.notificationForm.get('phoneNumber')?.setErrors(null);
  }

  save(): void {
    if (this.notificationForm.valid) {
      this.awaitingResponse = true;
      const webhook = this.notificationForm.value;
      const statuses = [];
      const requests = [];

      if (this.closed) {
        statuses.push(RecommendationStatus.Closed);
      }

      if (this.created) {
        statuses.push(RecommendationStatus.New);
      }

      webhook.workflowChange.value = statuses;

      const phoneOperation$: Observable<any> = this.usersService
        .validateUserPhoneNumber(webhook.phoneNumber)
        .pipe(
          switchMap(({ message, valid, phone }) =>
            valid
              ? this.usersService.patchCurrentUser({
                phone,
                smsConsent: webhook.smsConsent
              } as CurrentUser)
              : throwError(() => message)
          ),
          catchError((message) => {
            this.awaitingResponse = false;
            this.notificationForm
              .get('phoneNumber')
              ?.setErrors({ twilioValidation: message });

            return throwError(() => null);
          })
        );

      const { phone, smsConsent } = this.userInfoService.info$.getValue();
      if ((!phone.length || !smsConsent) && webhook.notificationType.sms) {
        requests.push(phoneOperation$);
      }

      if (!this.all) {
        if (this.created && this.closed) {
          webhook.name = $localize`Recommendation created or closed`;
        } else if (this.created) {
          webhook.name = $localize`Recommendation created`;
        } else if (this.closed) {
          webhook.name = $localize`Recommendation closed`;
        }
        requests.push(this.integrationsService.createUserWebhook(webhook));
      } else {
        requests.push(
          forkJoin([
            this.integrationsService.createUserWebhook({
              ...webhook,
              name: $localize`Recommendation updated`,
              workflowChange: {
                type: WebhookWorkflowChangeType.StatusChange,
                value: Object.values(RecommendationStatus).filter(
                  (s) => s !== RecommendationStatus.Draft
                )
              }
            }),
            this.integrationsService.createUserWebhook({
              ...webhook,
              name: $localize`Recommendation cost avoidance updated`,
              workflowChange: {
                type: WebhookWorkflowChangeType.CostAvoidanceChange,
                value: null
              }
            }),
            this.integrationsService.createUserWebhook({
              ...webhook,
              name: $localize`Recommendation alerts, comments, or attachments updated`,
              workflowChange: {
                type: WebhookWorkflowChangeType.ObjectAdded,
                value: Object.values(WebhookWorkflowObjectAddedType)
              }
            })
          ])
        );
      }

      from(requests)
        .pipe(
          concatAll(),
          takeUntil(this.ngUnsubscribe),
          catchError(() => of()),
          tap(() => {
            this.dialogRef.close();
            this.awaitingResponse = false;
          })
        )
        .subscribe();
    }
  }

  smsChanged() {
    const smsConsent = this.notificationForm.get('smsConsent');
    const phoneNumber = this.notificationForm.get('phoneNumber');
    const sms = this.notificationForm
      .get('notificationType')
      ?.get('sms')?.value;

    if (sms) {
      smsConsent?.setValidators([Validators.requiredTrue]);
      phoneNumber?.setValidators([Validators.required]);
    } else {
      smsConsent?.setValidators([]);
      phoneNumber?.setValidators([]);
      smsConsent?.setErrors(null);
      phoneNumber?.setErrors(null);
    }

    smsConsent?.updateValueAndValidity();
    phoneNumber?.updateValueAndValidity();
  }
}
